/*
  assets/cart-drawer.js — CART-DRAWER-RERENDER-COMMERCE-10
  Updated: 2026-09-10

  Owner:
  - <cart-drawer> open/close lifecycle and focus management.
  - Line item quantity, remove delegation and queueing.
  - Native Shopify cart section refresh and event publishing.
  - Post-render integration with window.GCityPartnersServiceProductProperties.

  Note:
  - Shopify cart endpoints only.
  - No GFun mutation, external API, checkout routing or price conversion authority.
*/
(function cartDrawerRuntime(window, document) {
  'use strict';

  var w = window;
  var d = document;
  if (!w || !d || !w.customElements) return;

  var VERSION = 'CART-DRAWER-RERENDER-COMMERCE-10';
  var CART_SELECTOR = 'cart-drawer';
  var CART_DRAWER_SELECTOR = '#CartDrawer';
  var CART_OVERLAY_SELECTOR = '#CartDrawer-Overlay';
  var CART_ICON_SELECTOR = '#cart-icon-bubble';
  var CART_ACTION_SELECTOR = [
    '[data-action]',
    '[data-cart-qty-btn]',
    '[data-cart-quantity-input]',
    '[data-cart-remove]',
    '[data-line]',
    'quantity-input',
    'cart-remove-button',
    '.quantity__button',
    '.quantity__input'
  ].join(',');

  function clean(value) {
    return String(value == null ? '' : value).trim();
  }

  function qs(selector, root) {
    return (root || d).querySelector(selector);
  }

  function qsa(selector, root) {
    return Array.prototype.slice.call((root || d).querySelectorAll(selector));
  }

  function warn() {
    if (w.console && typeof w.console.warn === 'function') {
      w.console.warn.apply(w.console, arguments);
    }
  }

  function safeFocus(element) {
    if (!element || typeof element.focus !== 'function') return;
    try { element.focus({ preventScroll: true }); }
    catch (_) { element.focus(); }
  }

  function cartRoot() {
    var root = (w.Shopify && w.Shopify.routes && w.Shopify.routes.root) || '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  function cartPageUrl() {
    return (w.routes && w.routes.cart_url) || (cartRoot() + 'cart');
  }

  function cartChangeUrl() {
    return (w.routes && w.routes.cart_change_url) || (cartRoot() + 'cart/change.js');
  }

  function publishCartUpdated(cart, source) {
    var detail = { cart: cart || {}, source: source || 'cart-drawer' };
    try { d.dispatchEvent(new CustomEvent('cart:updated', { detail: detail })); } catch (_) {}

    if (typeof w.publish === 'function' && w.PUB_SUB_EVENTS && w.PUB_SUB_EVENTS.cartUpdate) {
      try {
        w.publish(w.PUB_SUB_EVENTS.cartUpdate, { source: detail.source, cartData: cart || {} });
      } catch (_) {}
    }
  }

  function drawerItemCount(drawer) {
    var datasetCount = Number(drawer && drawer.dataset ? drawer.dataset.cartItemCount : NaN);
    if (Number.isFinite(datasetCount)) return datasetCount;

    return qsa('[data-cart-quantity-input], .quantity__input', drawer).reduce(function (total, input) {
      var quantity = Number(input && input.value);
      return total + (Number.isFinite(quantity) && quantity > 0 ? quantity : 0);
    }, 0);
  }

  function dispatchRendered(drawer, source) {
    try {
      d.dispatchEvent(new CustomEvent('gcity:cart-drawer:rendered', {
        detail: { source: source || 'cart-drawer.js', drawer: drawer, itemCount: drawerItemCount(drawer) }
      }));
    } catch (_) {}
  }

  function parseSectionNode(html, selector) {
    if (!html || !selector) return null;
    var parsed = new DOMParser().parseFromString(String(html), 'text/html');
    return parsed.querySelector(selector);
  }

  function trapFocusSafe(container, focusElement) {
    if (!container) return function noop() {};

    var gcityA11y = w.GCITY && w.GCITY.a11y;
    if (gcityA11y && typeof gcityA11y.trapFocus === 'function') {
      var releaseGcity = gcityA11y.trapFocus(container, focusElement || container);
      if (typeof releaseGcity === 'function') return releaseGcity;
    }

    if (typeof w.trapFocus === 'function') {
      var releaseGlobal = w.trapFocus(container, focusElement || container);
      if (typeof releaseGlobal === 'function') return releaseGlobal;
    }

    safeFocus(focusElement || container);
    return function noop() {};
  }

  function removeTrapFocusSafe(focusElement) {
    if (typeof w.removeTrapFocus === 'function') {
      try { w.removeTrapFocus(focusElement || null); } catch (_) {}
      return;
    }
    safeFocus(focusElement);
  }

  function parsePositiveLine(value) {
    var line = Number(value);
    return Number.isFinite(line) && Number.isInteger(line) && line > 0 ? line : 0;
  }

  function parseQuantityValue(value) {
    if (value == null) return null;
    var raw = clean(value);
    if (!raw) return null;
    var quantity = Number(raw);
    return Number.isFinite(quantity) && Number.isInteger(quantity) && quantity >= 0 ? quantity : null;
  }

  function inputIsValid(input) {
    return !(input && input.validity && input.validity.valid === false);
  }

  function lineReferenceFromElement(element) {
    if (!element) return null;
    var container = element.closest
      ? element.closest('[data-cart-line-key], [data-line], [data-index], quantity-input, cart-remove-button')
      : null;

    var lineKey = clean(element.dataset ? element.dataset.cartLineKey : element.getAttribute('data-cart-line-key'));
    if (!lineKey && container) {
      lineKey = clean(container.dataset ? container.dataset.cartLineKey : container.getAttribute('data-cart-line-key'));
    }
    if (lineKey) return { id: lineKey };

    var candidates = [
      element.dataset ? element.dataset.index : '',
      element.getAttribute ? element.getAttribute('data-index') : ''
    ];
    if (container) {
      candidates.push(container.getAttribute('data-line'));
      candidates.push(container.dataset ? container.dataset.line : '');
      candidates.push(container.dataset ? container.dataset.index : '');
      candidates.push(container.getAttribute('data-index'));
    }

    for (var i = 0; i < candidates.length; i += 1) {
      var line = parsePositiveLine(candidates[i]);
      if (line) return { line: line };
    }
    return null;
  }

  function normalizeLineReference(reference) {
    if (!reference) return null;
    if (typeof reference === 'object' && reference.id) return { id: String(reference.id) };
    var line = parsePositiveLine(typeof reference === 'object' ? reference.line : reference);
    return line ? { line: line } : null;
  }

  function lineReferenceKey(reference) {
    return reference && reference.id ? 'id:' + reference.id : 'line:' + String(reference && reference.line || 0);
  }

  function createLoadingOverlay(drawer) {
    var inner = qs('.drawer__inner', drawer);
    if (!inner) return null;

    var existing = qs('.loading-overlay[data-cart-drawer-loading]', inner);
    if (existing) return existing;

    var overlay = d.createElement('div');
    overlay.className = 'loading-overlay';
    overlay.setAttribute('data-cart-drawer-loading', 'true');
    overlay.setAttribute('aria-hidden', 'true');
    inner.appendChild(overlay);
    return overlay;
  }

  function currentDrawer() {
    return qs(CART_SELECTOR);
  }

  function bindCartIcon() {
    var cartLink = qs(CART_ICON_SELECTOR);
    if (!cartLink) return;

    cartLink.setAttribute('aria-haspopup', 'dialog');
    if (!cartLink.hasAttribute('aria-expanded')) cartLink.setAttribute('aria-expanded', 'false');

    if (!cartLink.__gcityCartDrawerClick) {
      cartLink.__gcityCartDrawerClick = function openFromClick(event) {
        var drawer = currentDrawer();
        if (!drawer || typeof drawer.open !== 'function') return;
        event.preventDefault();
        drawer.open(cartLink);
      };
      cartLink.addEventListener('click', cartLink.__gcityCartDrawerClick);
    }

    if (!cartLink.__gcityCartDrawerKeydown) {
      cartLink.__gcityCartDrawerKeydown = function openFromKeyboard(event) {
        var key = event.key || event.code;
        if (key !== ' ' && key !== 'Space' && key !== 'Spacebar') return;
        var drawer = currentDrawer();
        if (!drawer || typeof drawer.open !== 'function') return;
        event.preventDefault();
        drawer.open(cartLink);
      };
      cartLink.addEventListener('keydown', cartLink.__gcityCartDrawerKeydown);
    }
  }

  if (!w.customElements.get(CART_SELECTOR)) {
    w.customElements.define(CART_SELECTOR, class CartDrawerElement extends HTMLElement {
      constructor() {
        super();
        this.activeElement = null;
        this.releaseFocusTrap = null;
        this.overlay = null;
        this.container = null;
        this.loadingOverlay = null;
        this.__rootEventsBound = false;
        this.__actionEventsBound = false;
        this.__inputDebounce = null;
        this.__renderTimer = null;
        this.__focusTimer = null;
        this.__openFrame = null;
        this.__renderSequence = 0;
        this.__lineRequestController = null;
        this.__lineUpdateQueue = new Map();
        this.__lineQueueRunning = false;
        this.__disabledState = new WeakMap();

        this.onOverlayClick = this.close.bind(this);
        this.onCloseButtonClick = this.close.bind(this);
        this.onEscape = this.handleEscape.bind(this);
        this.onCartActionClick = this.handleCartActionClick.bind(this);
        this.onCartActionChange = this.handleCartActionChange.bind(this);
      }

      connectedCallback() {
        this.hydrateAfterRender('connected');
      }

      disconnectedCallback() {
        this.cancelScheduledOpen();
        clearTimeout(this.__inputDebounce);
        clearTimeout(this.__renderTimer);

        this.removeEventListener('keyup', this.onEscape);
        this.removeEventListener('click', this.onCartActionClick);
        this.removeEventListener('change', this.onCartActionChange);
        if (this.overlay) this.overlay.removeEventListener('click', this.onOverlayClick);

        if (this.__lineRequestController) this.__lineRequestController.abort();
        this.__lineRequestController = null;
        this.__lineUpdateQueue.clear();
        this.__lineQueueRunning = false;

        if (typeof this.releaseFocusTrap === 'function') this.releaseFocusTrap();
        this.releaseFocusTrap = null;

        this.classList.remove('active');
        this.setAttribute('aria-hidden', 'true');
        if (!qs(CART_SELECTOR + '.active')) d.body.classList.remove('overflow-hidden');

        var cartLink = qs(CART_ICON_SELECTOR);
        if (cartLink) cartLink.setAttribute('aria-expanded', 'false');

        this.__rootEventsBound = false;
        this.__actionEventsBound = false;
      }

      cancelScheduledOpen() {
        if (this.__openFrame != null && typeof w.cancelAnimationFrame === 'function') {
          w.cancelAnimationFrame(this.__openFrame);
        }
        this.__openFrame = null;
        clearTimeout(this.__focusTimer);
        this.__focusTimer = null;
      }

      syncElements() {
        this.container = qs(CART_DRAWER_SELECTOR, this) || qs(CART_DRAWER_SELECTOR);
        this.overlay = qs(CART_OVERLAY_SELECTOR, this) || qs(CART_OVERLAY_SELECTOR);
        this.loadingOverlay = createLoadingOverlay(this);
      }

      getScopedRoot() {
        return this.container || this;
      }

      bindRootEvents() {
        if (!this.__rootEventsBound) {
          this.__rootEventsBound = true;
          this.addEventListener('keyup', this.onEscape);
        }
        if (this.overlay) {
          this.overlay.removeEventListener('click', this.onOverlayClick);
          this.overlay.addEventListener('click', this.onOverlayClick);
        }
      }

      bindCartActions() {
        if (this.__actionEventsBound) return;
        this.__actionEventsBound = true;
        this.addEventListener('click', this.onCartActionClick);
        this.addEventListener('change', this.onCartActionChange);
      }

      bindCloseButtons() {
        qsa('[data-drawer-close], .drawer__close', this).forEach(function bindButton(button) {
          button.removeEventListener('click', this.onCloseButtonClick);
          button.addEventListener('click', this.onCloseButtonClick);
        }, this);
      }

      hydrateAfterRender(source) {
        this.syncElements();
        this.bindRootEvents();
        this.bindCartActions();
        this.bindCloseButtons();
        bindCartIcon();

        this.__renderSequence += 1;
        this.dataset.cartDrawerHydrated = 'true';
        this.dataset.cartDrawerRenderSequence = String(this.__renderSequence);
        this.dataset.cartDrawerRenderSource = source || 'unknown';

        if (w.GCityPartnersServiceProductProperties && typeof w.GCityPartnersServiceProductProperties.init === 'function') {
          w.GCityPartnersServiceProductProperties.init(this);
        }

        dispatchRendered(this, source);
      }

      handleEscape(event) {
        if (event.key === 'Escape' || event.code === 'Escape') this.close();
      }

      setActiveElement(element) {
        this.activeElement = element || null;
      }

      open(triggeredBy) {
        this.hydrateAfterRender('open');
        this.cancelScheduledOpen();
        if (triggeredBy) this.setActiveElement(triggeredBy);

        var cartLink = qs(CART_ICON_SELECTOR);
        if (cartLink) cartLink.setAttribute('aria-expanded', 'true');

        if (typeof this.releaseFocusTrap === 'function') this.releaseFocusTrap();
        this.releaseFocusTrap = null;

        this.__openFrame = w.requestAnimationFrame(function activateDrawer() {
          this.__openFrame = null;
          this.classList.add('animate', 'active');
          this.setAttribute('aria-hidden', 'false');
        }.bind(this));

        d.body.classList.add('overflow-hidden');

        this.__focusTimer = w.setTimeout(function focusDrawer() {
          this.__focusTimer = null;
          if (!this.classList.contains('active')) return;

          const trapContainer =
            qs('.drawer__inner', this) || this.container || this;
          const focusTarget =
            qs('.drawer__close', trapContainer) || trapContainer;

          this.releaseFocusTrap =
            trapFocusSafe(trapContainer, focusTarget);
        }.bind(this), 80);
      }

      close(event, elementToFocus) {
        if (event && typeof event.preventDefault === 'function') event.preventDefault();
        this.cancelScheduledOpen();

        this.classList.remove('active');
        this.setAttribute('aria-hidden', 'true');
        if (!qs(CART_SELECTOR + '.active')) d.body.classList.remove('overflow-hidden');

        var cartLink = qs(CART_ICON_SELECTOR);
        if (cartLink) cartLink.setAttribute('aria-expanded', 'false');

        if (typeof this.releaseFocusTrap === 'function') this.releaseFocusTrap();
        this.releaseFocusTrap = null;
        removeTrapFocusSafe(elementToFocus || this.activeElement);
      }

      getSectionsToRender() {
        return [
          { id: 'cart-drawer', selector: CART_DRAWER_SELECTOR },
          { id: 'cart-icon-bubble', selector: CART_ICON_SELECTOR }
        ];
      }

      updateEmptyState(parsedState) {
        var itemCount = Number(parsedState && parsedState.item_count);
        if (!Number.isFinite(itemCount) && parsedState && Array.isArray(parsedState.items)) {
          itemCount = parsedState.items.reduce(function total(sum, item) {
            return sum + Number(item && item.quantity || 0);
          }, 0);
        }
        if (!Number.isFinite(itemCount)) return;

        this.dataset.cartItemCount = String(itemCount);
        this.classList.toggle('is-empty', itemCount === 0);
      }

      renderSection(section, html) {
        var element = section.selector ? qs(section.selector) : d.getElementById(section.id);
        var replacement = parseSectionNode(html, section.selector);
        if (!element || !replacement) {
          warn('[Cart Drawer] Missing section selector:', section && section.selector);
          return false;
        }

        element.innerHTML = replacement.innerHTML;

        if (section.id === 'cart-drawer') {
          var parsedRoot = replacement.closest && replacement.closest(CART_SELECTOR);
          if (!parsedRoot) {
            var parsedDocument = new DOMParser().parseFromString(String(html), 'text/html');
            parsedRoot = parsedDocument.querySelector(CART_SELECTOR);
          }
          if (parsedRoot) {
            this.classList.toggle('is-empty', parsedRoot.classList.contains('is-empty'));
            ['data-checkout-enabled', 'data-checkout-blocked-item-count', 'data-cart-error-message'].forEach(function (attribute) {
              if (parsedRoot.hasAttribute(attribute)) {
                this.setAttribute(attribute, parsedRoot.getAttribute(attribute));
              } else {
                this.removeAttribute(attribute);
              }
            }, this);
          }
        }

        return true;
      }

      renderContents(parsedState, preserveLoading) {
        parsedState = parsedState || {};
        this.productId = parsedState.id || '';
        this.updateEmptyState(parsedState);

        this.getSectionsToRender().forEach(function render(section) {
          var html = parsedState.sections && parsedState.sections[section.id];
          if (html) this.renderSection(section, html);
        }, this);

        clearTimeout(this.__renderTimer);
        this.__renderTimer = w.setTimeout(function recoverAfterRender() {
          this.__renderTimer = null;
          this.hydrateAfterRender('renderContents');
          if (!preserveLoading) this.setLoading(false);
          this.open();
        }.bind(this), 0);
      }

      handleCartActionClick(event) {
        var target = event.target;
        if (!target || !target.closest) return;

        var root = target.closest(CART_SELECTOR) || target.closest(CART_DRAWER_SELECTOR);
        if (root !== this && root !== this.container && !this.contains(target)) return;

        var action = target.closest(CART_ACTION_SELECTOR);
        if (!action || !this.contains(action)) return;

        var removeControl = target.closest('[data-cart-remove], cart-remove-button');
        if (removeControl && this.contains(removeControl)) {
          event.preventDefault();
          this.handleRemove(removeControl);
          return;
        }

        var quantityButton = target.closest('[data-cart-qty-btn], .quantity__button');
        if (quantityButton && this.contains(quantityButton)) {
          event.preventDefault();
          this.handleQuantityButton(quantityButton);
        }
      }

      handleCartActionChange(event) {
        var target = event.target;
        if (!target || !target.closest || !this.contains(target)) return;
        var input = target.closest('[data-cart-quantity-input], .quantity__input');
        if (!input || !this.contains(input)) return;
        this.handleQuantityInput(input);
      }

      handleQuantityButton(button) {
        if (!button || button.disabled) return;
        var container = button.closest('quantity-input, [data-cart-line-key], [data-line], [data-index]');
        var input = container
          ? (qs('[data-cart-quantity-input], .quantity__input', container) || qs('input[type="number"]', container))
          : null;
        if (!input || input.disabled) return;

        var reference = lineReferenceFromElement(input) || lineReferenceFromElement(button);
        if (!reference) return;

        // Existing quantity-input component, when registered, owns stepping.
        // Otherwise this drawer handler supplies the missing step behavior.
        if (!w.customElements.get('quantity-input')) {
          const previousValue = input.value;

          try {
            if (button.name === 'plus') {
              input.stepUp();
            } else if (button.name === 'minus') {
              input.stepDown();
            } else {
              return;
            }
          } catch (error) {
            warn('[Cart Drawer] Invalid quantity step:', error);
            return;
          }

          if (input.value === previousValue) return;
        }

        clearTimeout(this.__inputDebounce);
        this.__inputDebounce = w.setTimeout(() => {
          if (!inputIsValid(input)) return;

          const quantity = parseQuantityValue(input.value);
          if (quantity === null) return;

          this.updateLineItem(reference, quantity);
        }, 120);
      }

      handleQuantityInput(input) {
        if (!input || input.disabled || !inputIsValid(input)) return;
        var reference = lineReferenceFromElement(input);
        var quantity = parseQuantityValue(input.value);
        if (!reference || quantity === null) return;

        clearTimeout(this.__inputDebounce);
        this.__inputDebounce = w.setTimeout(function updateInput() {
          this.updateLineItem(reference, quantity);
        }.bind(this), 120);
      }

      handleRemove(removeControl) {
        var reference = lineReferenceFromElement(removeControl);
        if (!reference) return;
        clearTimeout(this.__inputDebounce);
        this.__lineUpdateQueue.delete(lineReferenceKey(reference));
        this.updateLineItem(reference, 0);
      }

      updateLineItem(reference, quantity) {
        reference = normalizeLineReference(reference);
        quantity = parseQuantityValue(quantity);
        if (!reference || quantity === null) return;

        this.__lineUpdateQueue.set(lineReferenceKey(reference), {
          reference: reference,
          quantity: quantity
        });
        this.processLineItemQueue();
      }

      async processLineItemQueue() {
        if (this.__lineQueueRunning) return;
        this.__lineQueueRunning = true;
        this.setLoading(true);

        try {
          while (this.__lineUpdateQueue.size > 0) {
            var queuedEntry = this.__lineUpdateQueue.entries().next().value;
            var queueKey = queuedEntry[0];
            var update = queuedEntry[1];
            this.__lineUpdateQueue.delete(queueKey);

            this.__lineRequestController = typeof AbortController === 'function' ? new AbortController() : null;
            var controller = this.__lineRequestController;
            var change = { quantity: update.quantity };
            if (update.reference.id) change.id = update.reference.id;
            else change.line = update.reference.line;
            change.sections = this.getSectionsToRender().map(function mapSection(section) {
              return section.id;
            });
            change.sections_url = w.location.pathname;

            var requestOptions = {
              method: 'POST',
              credentials: 'same-origin',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
              },
              body: JSON.stringify(change)
            };
            if (controller) requestOptions.signal = controller.signal;

            var response = await fetch(cartChangeUrl(), requestOptions);
            var state = {};
            try { state = await response.json(); } catch (_) {}

            if (!response.ok) {
              throw new Error(state && state.description ? state.description : 'cart_update_failed');
            }

            if (this.__lineUpdateQueue.size === 0) {
              if (state.sections) {
                this.renderContents(state, true);
              } else {
                await this.refreshSections();
                this.updateEmptyState(state);
                this.open();
              }
              publishCartUpdated(state, 'cart-drawer');
            }
          }
        } catch (error) {
          if (!error || error.name !== 'AbortError') {
            warn('[Cart Drawer] Update error:', error);
            try { await this.refreshSections(); }
            catch (refreshError) { warn('[Cart Drawer] Recovery error:', refreshError); }
            this.setError(this.dataset.cartErrorMessage || 'Cart update failed. Please try again.');
            this.toast(this.dataset.cartErrorMessage || 'Cart update failed. Please try again.', 'error');
          }
          this.__lineUpdateQueue.clear();
        } finally {
          this.__lineRequestController = null;
          this.__lineQueueRunning = false;
          this.setLoading(false);
        }
      }

      async refreshSections() {
        var sectionIds = this.getSectionsToRender().map(function mapSection(section) {
          return section.id;
        }).join(',');

        var separator = cartPageUrl().indexOf('?') === -1 ? '?' : '&';
        var url = cartPageUrl() + separator + 'sections=' + encodeURIComponent(sectionIds);
        var response = await fetch(url, {
          method: 'GET',
          credentials: 'same-origin',
          headers: { 'Accept': 'application/json' }
        });
        if (!response.ok) throw new Error('cart_section_refresh_failed');

        var sections = await response.json();
        this.getSectionsToRender().forEach(function render(section) {
          var html = sections && sections[section.id];
          if (html) this.renderSection(section, html);
        }, this);
        this.hydrateAfterRender('refreshSections');
      }

      setError(message) {
        var region = qs('#CartDrawer-CartErrors', this);
        if (region) region.textContent = String(message || '');
      }

      setLoading(isLoading) {
        this.syncElements();
        this.setAttribute('aria-busy', isLoading ? 'true' : 'false');
        this.dataset.cartDrawerLoading = isLoading ? 'true' : 'false';

        if (this.loadingOverlay) {
          this.loadingOverlay.style.display = isLoading ? 'flex' : '';
          this.loadingOverlay.setAttribute('aria-hidden', isLoading ? 'false' : 'true');
        }

        qsa('[data-cart-qty-btn], [data-cart-quantity-input], [data-cart-remove], .quantity__button, .quantity__input', this.getScopedRoot()).forEach(function toggleControl(control) {
          if (control.closest && control.closest('.shopify-payment-button')) return;
          if (!('disabled' in control)) return;

          if (isLoading) {
            if (!this.__disabledState.has(control)) {
              this.__disabledState.set(control, Boolean(control.disabled));
            }
            control.disabled = true;
            control.setAttribute('aria-disabled', 'true');
            return;
          }

          if (this.__disabledState.has(control)) {
            control.disabled = this.__disabledState.get(control);
            this.__disabledState.delete(control);
          }
          control.setAttribute('aria-disabled', control.disabled ? 'true' : 'false');
        }, this);

        if (isLoading) this.setError('');
      }

      toast(message, type) {
        if (w.GCITY && w.GCITY.ui && typeof w.GCITY.ui.toast === 'function') {
          w.GCITY.ui.toast(message, type || 'info');
          return;
        }

        var node = d.createElement('div');
        node.textContent = String(message || '');
        node.setAttribute('role', type === 'error' ? 'alert' : 'status');
        node.setAttribute('aria-live', type === 'error' ? 'assertive' : 'polite');
        node.className = 'cart-drawer-toast cart-drawer-toast--' + String(type || 'info');
        d.body.appendChild(node);
        w.setTimeout(function removeToast() {
          try { node.remove(); } catch (_) {}
        }, 2800);
      }
    });
  }

  bindCartIcon();

  try {
    d.dispatchEvent(new CustomEvent('gcity:cart-drawer-ready', {
      detail: { source: 'cart-drawer.js', contract: VERSION }
    }));
  } catch (_) {}
})(window, document);